import { useState, useEffect, useContext, useCallback } from "react";
import styles from "./styles.module.css";
import { useNavigate } from "react-router-dom";
import {
  ROUTES,
  STORAGE_KEYS,
  SHIFT_SYNC_STATUS,
  INVOICE_FORMATS,
  SHIFT_REPORT_TYPES,
  SHIFT_REPORT_OPTIONS,
} from "const";
import ButtonRed from "components/utils/CustomFields/ButtonRed";
import ButtonGray from "components/utils/CustomFields/ButtonGray";
import CustomCheckbox from "components/utils/CustomFields/CustomCheckbox";
import ClearDataConfirmDialog from "./ClearDataConfirmDialog";
import * as ReportHelper from "helpers/ReportHelper";
import * as SalesHelper from "helpers/SalesHelper";
import * as TerminalHelper from "helpers/TermialHelper";
import ShopContext from "context/ShopContext";
import ShiftService from "services/ShiftService";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import * as ShiftHelper from "helpers/ShiftHelper";
import { roundOff } from "helpers/NumberFormatHelper";
import toast from "react-hot-toast";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";

export default function CloseShiftDialog({ open, onClose, onSubmit, redirectRoute = ROUTES.SHIFT }) {
  const navigate = useNavigate();
  const { shop } = useContext(ShopContext);

  const [step, setStep] = useState(0);
  const [paymentModes, setPaymentModes] = useState([]);
  const [enteredBalances, setEnteredBalances] = useState({});
  const [isPrinting, setIsPrinting] = useState(false);
  const [clearAllData, setClearAllData] = useState(false);
  const [showConfirmClear, setShowConfirmClear] = useState(false);
  const [selectedReports, setSelectedReports] = useState({
    [SHIFT_REPORT_TYPES.SALE_SUMMARY]: false,
    [SHIFT_REPORT_TYPES.ITEM_SUMMARY]: false,
    [SHIFT_REPORT_TYPES.SHIFT_REPORT]: false,
  });

  const initDialog = useCallback(async () => {
    try {
      await SalesHelper.syncPendingSales();
      const shiftId = localStorage.getItem(STORAGE_KEYS.SHIFT_ID);
      const shiftData = await ShiftHelper.loadShiftHelper(shiftId);

      const shiftPaymentModes = shiftData.paymentModeSummaries || [];

      setPaymentModes(shiftPaymentModes);

      const isClosing = ShiftHelper.isShiftClosing();
      const savedBalances = ShiftHelper.getShiftClosingEnteredBalances();

      if (isClosing && savedBalances) {
        setEnteredBalances(savedBalances);
        setStep(2);
      } else {
        const initialEntered = {};
        shiftPaymentModes.forEach((paymentMode) => {
          initialEntered[paymentMode.id] = "";
        });
        setEnteredBalances(initialEntered);
        setStep(1);
      }
    } catch (e) {
      console.error("Failed to load shift data", e);
      toast.error("Failed to initialize shift closure");
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (open) {
      setStep(0);
      setPaymentModes([]);
      setEnteredBalances({});
      initDialog();
    }
  }, [open, initDialog]);

  const handleAmountChange = (modeId, value) => {
    setEnteredBalances((prev) => ({
      ...prev,
      [modeId]: value,
    }));
  };

  const handlePrintReport = async (reportKey) => {
    try {
      const shiftId = localStorage.getItem(STORAGE_KEYS.SHIFT_ID);
      const terminalSettings = TerminalHelper.getTerminalSettings();
      const invoiceFormat = terminalSettings.invoicePrinterFormat || INVOICE_FORMATS.THREE_INCH;
      const isMulti =
        invoiceFormat === INVOICE_FORMATS.THREE_INCH_MULTI_LANGUAGE_ARABIC ||
        invoiceFormat === INVOICE_FORMATS.THREE_INCH_MULTI_LANGUAGE;

      if (reportKey === SHIFT_REPORT_TYPES.SALE_SUMMARY) {
        await printSalesSummary(shiftId, shop, isMulti);
      } else if (reportKey === SHIFT_REPORT_TYPES.ITEM_SUMMARY) {
        await printItemSummary(shiftId, shop, isMulti);
      } else if (reportKey === SHIFT_REPORT_TYPES.SHIFT_REPORT) {
        await printShiftOverview(shiftId, enteredBalances);
      }
    } catch (e) {
      console.error(`Failed to print ${reportKey}`, e);
      toast.error("Failed to print report");
    }
  };

  const handleReportToggle = (reportKey) => {
    const isChecked = !selectedReports[reportKey];
    setSelectedReports((prev) => ({
      ...prev,
      [reportKey]: isChecked,
    }));

    if (isChecked) {
      handlePrintReport(reportKey);
    }
  };

  const handleProceed = () => {
    ShiftHelper.setShiftClosingState(enteredBalances);
    setStep(2);
  };

  const handleClearCheckboxClick = () => {
    if (clearAllData) {
      setClearAllData(false);
    } else {
      setShowConfirmClear(true);
    }
  };

  async function printSalesSummary(shiftId, shop, isMulti) {
    const sales = await ReportHelper.getSaleSummaryForShift({ shiftId });
    const bytes = isMulti
      ? await ReportHelper.getSalesSummaryMultiThreeInch(sales, shop, shiftId)
      : await ReportHelper.getSalesSummaryReport(sales, shop, shiftId);
    await ReportHelper.printReport({ reportText: bytes });
  }

  async function printItemSummary(shiftId, shop, isMulti) {
    const itemRows = await ReportHelper.getItemsSummaryForShift(shiftId);
    const bytes = isMulti
      ? await ReportHelper.getItemSummaryMultiThreeInch(itemRows, shop, shiftId)
      : await ReportHelper.getItemSummaryReport(itemRows, shop, shiftId);
    await ReportHelper.printReport({ reportText: bytes });
  }

  async function printShiftOverview(shiftId, entered) {
    const shiftData = await ShiftHelper.loadShiftHelper(shiftId);
    const bytes = await ReportHelper.getShiftReportPrintText({
      shiftOverview: shiftData?.shiftOverview,
      paymentModeSummaries: shiftData?.paymentModeSummaries,
      enteredBalances: entered,
    });
    await ReportHelper.printReport({ reportText: bytes });
  }

  const handleCloseShiftAction = async () => {
    setIsPrinting(true);
    const shiftId = localStorage.getItem(STORAGE_KEYS.SHIFT_ID);

    try {
      const closedAt = DateTimeHelpers.getLocalISOString();
      const { payload, tenders: apiTenders } = await ShiftHelper.buildCloseShiftPayload(shiftId, closedAt);

      let syncStatus = null;
      try {
        await ShiftService.closeShift(shiftId, payload);
        toast.success("Shift closed successfully!");
      } catch (err) {
        console.error("Failed to close shift via API", err);
        syncStatus = SHIFT_SYNC_STATUS.PENDING_CLOSE;
        toast.success("Shift closed offline.");
      }

      const isReloading = await ShiftHelper.finalizeShiftClosureState(
        shiftId,
        payload,
        apiTenders,
        syncStatus,
        clearAllData,
      );
      if (isReloading) return;
    } catch (e) {
      console.error("Failed to process shift closure", e);
      toast.error("Failed to process shift closure");
    } finally {
      setIsPrinting(false);
    }

    if (onSubmit) onSubmit();
    onClose();
    if (redirectRoute) {
      navigate(redirectRoute);
    }
  };

  if (!open) return null;

  return (
    <div className={styles.popUp}>
      <div className={styles.contentWrapper}>
        <div className={styles.headInfoSec}>
          <h2>{step === 2 ? "Expected Closing Balance" : "Close Shift"}</h2>
        </div>

        <div className={styles.contentArea}>
          {isPrinting ? (
            <div style={{ padding: "40px 0" }}>
              <SkeletonLoading isOpen={isPrinting} count={9} height={60} lg={3} />
            </div>
          ) : (
            <>
              {step === 0 && (
                <div className={styles.syncingText}>
                  <h3 className={styles.subTitle}>Syncing sales to cloud...</h3>
                </div>
              )}

              {step === 1 && (
                <>
                  <h3 className={styles.subTitle}>Syncing sales to cloud, Please verify balances before closing.</h3>
                  <div className={styles.enterBalancesTitle}>Enter closing balances</div>
                  <div className={styles.inputList}>
                    {paymentModes.map((paymentMode) => (
                      <div key={paymentMode.id} className={styles.inputRow}>
                        <div className={styles.inputLabel}>{paymentMode.payment}</div>
                        <input
                          type="number"
                          placeholder="Enter amount"
                          className={styles.amountInput}
                          value={enteredBalances[paymentMode.id] || ""}
                          onChange={(e) => handleAmountChange(paymentMode.id, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <h3 className={styles.subTitle}>Compare entered amounts with system-calculated totals:</h3>

                  <div className={styles.comparisonTable}>
                    <div className={styles.compHeader}>
                      <div className={styles.compCol}>Entered Payments</div>
                      <div className={styles.compCol}>Actual</div>
                      <div className={styles.compCol}>Difference</div>
                    </div>

                    <div className={styles.compBody}>
                      {(() => {
                        const totalEntered = paymentModes.reduce((sum, paymentMode) => {
                          const enteredStr = enteredBalances[paymentMode.id];
                          return sum + (enteredStr !== "" ? Number(enteredStr) : 0);
                        }, 0);

                        const totalActual = paymentModes.reduce((sum, paymentMode) => {
                          return sum + (paymentMode.closing || 0);
                        }, 0);

                        const totalDiff = totalEntered - totalActual;

                        return (
                          <>
                            {paymentModes.map((paymentMode) => {
                              const enteredStr = enteredBalances[paymentMode.id];
                              const entered = enteredStr !== "" ? Number(enteredStr) : 0;
                              const actual = paymentMode.closing || 0;
                              const diff = entered - actual;

                              return (
                                <div key={paymentMode.id} className={styles.compRow}>
                                  <div className={styles.compCol}>
                                    <div className={styles.inputRowReadOnly}>
                                      <div className={styles.inputLabel}>{paymentMode.payment}</div>
                                      <div className={styles.inputValue}>
                                        {enteredStr !== "" ? roundOff(entered) : "0.0"}
                                      </div>
                                    </div>
                                  </div>
                                  <div className={styles.compCol}>
                                    <div
                                      className={`${styles.boxValue} ${diff !== 0 ? styles.bgRed : ""} ${actual < 0 ? styles.textRed : actual > 0 ? styles.textGreen : ""}`}
                                    >
                                      {roundOff(actual)}
                                    </div>
                                  </div>
                                  <div className={styles.compCol}>
                                    <div
                                      className={`${styles.boxValue} ${diff !== 0 ? styles.bgRed : ""} ${diff < 0 ? styles.textRed : diff > 0 ? styles.textGreen : ""}`}
                                    >
                                      {roundOff(diff)}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}

                            {/* Total Row */}
                            <div className={`${styles.compRow} ${styles.totalRow}`}>
                              <div className={styles.compCol}>
                                <div
                                  className={styles.inputRowReadOnly}
                                  style={{ backgroundColor: "#f0f0f2", borderColor: "#c0c0c2" }}
                                >
                                  <div
                                    className={styles.inputLabel}
                                    style={{ fontWeight: "bold", backgroundColor: "#d8d8da" }}
                                  >
                                    Total
                                  </div>
                                  <div className={styles.inputValue} style={{ fontWeight: "bold" }}>
                                    {roundOff(totalEntered)}
                                  </div>
                                </div>
                              </div>
                              <div className={styles.compCol}>
                                <div
                                  className={`${styles.boxValue} ${totalDiff !== 0 ? styles.bgRed : ""} ${totalActual < 0 ? styles.textRed : totalActual > 0 ? styles.textGreen : ""}`}
                                  style={{ fontWeight: "bold", borderColor: totalDiff !== 0 ? "#fbd6d9" : "#c0c0c2" }}
                                >
                                  {roundOff(totalActual)}
                                </div>
                              </div>
                              <div className={styles.compCol}>
                                <div
                                  className={`${styles.boxValue} ${totalDiff !== 0 ? styles.bgRed : ""} ${totalDiff < 0 ? styles.textRed : totalDiff > 0 ? styles.textGreen : ""}`}
                                  style={{ fontWeight: "bold", borderColor: totalDiff !== 0 ? "#fbd6d9" : "#c0c0c2" }}
                                >
                                  {roundOff(totalDiff)}
                                </div>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    <div className={styles.reportSelectionSection}>
                      {SHIFT_REPORT_OPTIONS.map((option) => (
                        <div
                          key={option.key}
                          className={styles.reportCheckbox}
                          onClick={() => handleReportToggle(option.key)}
                        >
                          <CustomCheckbox
                            checked={!!selectedReports[option.key]}
                            onChange={(e) => e.stopPropagation()}
                            style={{ padding: 0 }}
                          />
                          <span>{option.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {!isPrinting && (
          <div className={styles.buttonSection}>
            {step === 1 && (
              <>
                <ButtonGray onClick={onClose}>Cancel</ButtonGray>
                <ButtonRed onClick={handleProceed}>Proceed</ButtonRed>
              </>
            )}
            {step === 2 && (
              <>
                <div className={styles.clearDataCheckbox} onClick={handleClearCheckboxClick}>
                  <CustomCheckbox checked={clearAllData} onChange={(e) => e.stopPropagation()} style={{ padding: 0 }} />
                  <span>Clear all data</span>
                </div>
                <ButtonRed onClick={handleCloseShiftAction}>Close Shift</ButtonRed>
              </>
            )}
          </div>
        )}
      </div>

      <ClearDataConfirmDialog
        open={showConfirmClear}
        onClose={() => setShowConfirmClear(false)}
        onConfirm={() => {
          setClearAllData(true);
          setShowConfirmClear(false);
        }}
      />
    </div>
  );
}
